import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

// دالة بديلة لإنشاء معرف فريد
const generateUniqueId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

// تحويل ملف إلى base64
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = () => {
      if (reader.result) {
        resolve(reader.result.toString());
      } else {
        reject(new Error('فشل في تحويل الملف إلى base64'));
      }
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
  });
};

// رفع صورة إلى التخزين
export const uploadImage = async (file: File, folder: string = 'images'): Promise<string> => {
  try {
    console.log('بدء عملية رفع الصورة:', file.name, 'حجم:', (file.size / 1024).toFixed(2) + 'KB', 'نوع:', file.type);
    
    // التحقق من صلاحية نوع الملف
    if (!file.type.startsWith('image/')) {
      console.error('خطأ: الملف ليس صورة:', file.type);
      throw new Error('الملف المختار ليس صورة');
    }
    
    // التحقق من حجم الملف (10 ميجابايت كحد أقصى)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      console.error('خطأ: حجم الصورة كبير جدًا:', (file.size / (1024 * 1024)).toFixed(2) + 'MB');
      throw new Error('حجم الصورة أكبر من الحد المسموح (10 ميجابايت)');
    }
    
    const storage = getStorage();
    console.log('تم الحصول على مرجع Firebase Storage');
    
    const fileExtension = file.name.split('.').pop() || 'jpg';
    const fileName = `${generateUniqueId()}.${fileExtension}`;
    const fullPath = `${folder}/${fileName}`;
    console.log('مسار الملف على Firebase Storage:', fullPath);
    
    const storageRef = ref(storage, fullPath);
    
    // استخدام رفع متتبع التقدم
    console.log('بدء عملية رفع الصورة إلى Firebase Storage...');
    const uploadTask = uploadBytesResumable(storageRef, file);
    
    return new Promise<string>((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // يمكنك مراقبة التقدم هنا إذا لزم الأمر
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log(`تقدم الرفع: ${progress.toFixed(2)}%`);
        },
        (error) => {
          console.error('خطأ أثناء رفع الصورة:', error);
          console.error('رمز الخطأ:', error.code);
          console.error('رسالة الخطأ:', error.message);
          
          // معالجة أخطاء محددة
          if (error.code === 'storage/unauthorized') {
            reject(new Error('غير مصرح لك برفع الصور'));
          } else if (error.code === 'storage/canceled') {
            reject(new Error('تم إلغاء رفع الصورة'));
          } else if (error.code === 'storage/unknown') {
            reject(new Error('حدث خطأ غير معروف أثناء رفع الصورة'));
          } else if (error.code === 'storage/quota-exceeded') {
            reject(new Error('تم تجاوز الحد الأقصى للتخزين'));
          } else if (error.code === 'storage/invalid-url') {
            reject(new Error('رابط تخزين غير صالح'));
          } else if (error.code === 'storage/server-file-wrong-size') {
            reject(new Error('خطأ في حجم الملف'));
          } else {
            reject(error);
          }
        },
        async () => {
          try {
            console.log('اكتمل رفع الصورة بنجاح، جاري الحصول على رابط التنزيل...');
            // الحصول على رابط التنزيل
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('تم الحصول على رابط الصورة بنجاح:', downloadURL);
            resolve(downloadURL);
          } catch (urlError) {
            console.error('خطأ في الحصول على رابط التنزيل:', urlError);
            reject(new Error('تم رفع الصورة ولكن فشل في الحصول على الرابط'));
          }
        }
      );
    });
  } catch (error) {
    console.error('خطأ عام في رفع الصورة:', error);
    throw error;
  }
};

// تحميل صورة بواسطة رابط خارجي
export const uploadImageFromUrl = async (url: string, path: string): Promise<string> => {
  try {
    // تحميل الصورة من الرابط
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('فشل في تحميل الصورة من الرابط المحدد');
    }
    
    // تحويل الصورة إلى Blob
    const blob = await response.blob();
    
    // إنشاء كائن File من Blob
    const filename = url.substring(url.lastIndexOf('/') + 1);
    const file = new File([blob], filename, { type: blob.type });
    
    // تحميل الصورة إلى Firebase Storage
    return await uploadImage(file, path);
  } catch (error) {
    console.error('Error in uploadImageFromUrl:', error);
    throw error;
  }
}; 